import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPasswordPage,
  head: () => ({
    meta: [
      { title: "Reset your password — SmsEmpire" },
      { name: "description", content: "Request a link to reset your SmsEmpire password." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function ForgotPasswordPage() {
  const { t } = useI18n();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const email = new FormData(event.currentTarget).get("email");
    setSending(true);
    try {
      await api.post(
        "/api/auth/forgot-password",
        { email },
        { anonymous: true, skipAuthRedirect: true },
      );
      // The backend answers identically whether or not the address exists, so the UI
      // must not imply anything either.
      setSent(true);
    } catch (error) {
      toast.error(
        error instanceof ApiError && error.isRateLimited
          ? t("auth.reset_rate_limited")
          : t("common.error"),
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-primary shadow-glow">
            <Sparkles className="h-5 w-5 text-primary-foreground" aria-hidden />
          </div>
          <span className="text-lg font-bold tracking-tight">SmsEmpire</span>
        </Link>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-elegant">
          {sent ? (
            <div className="text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" aria-hidden />
              <h1 className="mt-4 text-xl font-semibold">{t("auth.reset_sent_title")}</h1>
              <p className="mt-2 text-sm text-muted-foreground">{t("auth.reset_sent_body")}</p>
              <Button asChild variant="outline" className="mt-6 w-full">
                <Link to="/auth" search={{ tab: "login" }}>
                  {t("auth.back_to_login")}
                </Link>
              </Button>
            </div>
          ) : (
            <>
              <h1 className="text-xl font-semibold">{t("auth.forgot_title")}</h1>
              <p className="mt-2 text-sm text-muted-foreground">{t("auth.forgot_subtitle")}</p>
              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fp-email">{t("auth.email")}</Label>
                  <Input
                    id="fp-email"
                    name="email"
                    type="email"
                    required
                    maxLength={254}
                    autoComplete="email"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-gradient-primary shadow-glow"
                >
                  {sending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                      {t("auth.sending")}
                    </>
                  ) : (
                    t("auth.send_reset_link")
                  )}
                </Button>
              </form>
              <p className="mt-4 text-center text-sm">
                <Link
                  to="/auth"
                  search={{ tab: "login" }}
                  className="text-muted-foreground underline-offset-4 hover:text-primary hover:underline"
                >
                  {t("auth.back_to_login")}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
